import { faBuilding } from '@fortawesome/pro-light-svg-icons';
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { fetchTasks } from '@/api/dashboard';
import { HelpSheet } from '@/components/shared/HelpSheet';
import { EmptyState } from '@/components/ui/empty-state';
import { useNetworkMapCompanies } from '@/hooks/useNetworkMapCompanies';
import { ensurePermissions, usePermissions } from '@/hooks/usePermissions';
import { useTranslator } from '@/hooks/useTranslator';
import { buildCountryMarkers } from '@/lib/utils/networkMapHelpers';
import { queryClient } from '@/lib/utils/queryClient';
import { useSessionStore } from '@/store/sessionStore';
import type { ActivityCardTab } from '../-components/ActivityCard';
import ActivityCard from '../-components/ActivityCard';
import KpiCards from '../-components/KpiCards';
import MapCard from '../-components/MapCard';
import { useDashboardNotifications } from '../-hooks/useDashboardNotifications';
import { useDashboardTasks } from '../-hooks/useDashboardTasks';
import { useDismissTask } from '../-hooks/useDismissTask';
import { useNetworkCount } from '../-hooks/useNetworkCount';

export const Route = createFileRoute('/(client)/dashboard/(pages)/')({
  // `GET /api/notifications` (which backs the tasks list) requires
  // `network:read` regardless of trigger type — pre-fetching it
  // unconditionally in the loader is exactly the shape of bug that crashed
  // this route for a user without it.
  loader: async () => {
    const perms = await ensurePermissions();
    if (!perms.network.canRead) return;
    return queryClient.ensureQueryData({
      queryKey: ['dashboard', 'tasks'],
      queryFn: fetchTasks,
    });
  },
  component: DashboardPage,
});

function DashboardPage() {
  const user = useSessionStore(s => s.user);
  const { getTranslation: t } = useTranslator({ page: 'dashboard' });
  const { changeStatus } = useDismissTask();
  const navigate = useNavigate();
  const openActivityFeed = () => navigate({ to: '/activity-feed' });
  const [helpOpen, setHelpOpen] = useState(false);
  const [feedTab, setFeedTab] = useState<ActivityCardTab>('tasks');

  const canReadNetwork = usePermissions().network.canRead;
  const { data: tasksData } = useDashboardTasks({ enabled: canReadNetwork });
  const { data: companyCount = 0 } = useNetworkCount({
    enabled: canReadNetwork,
  });
  // The map and the Countries KPI both read the network companies, so every
  // number in this view counts the same set. The KPI used to come from the
  // risk-map endpoint, which tallied with neither the map nor the Companies KPI.
  const { data: mapCompanies, isLoading: mapLoading } = useNetworkMapCompanies(
    {},
    { enabled: canReadNetwork },
  );
  const companies = mapCompanies?.data ?? [];
  const countryCount = buildCountryMarkers(companies).length;
  const { notifications, isLoading: isNotificationsLoading } =
    useDashboardNotifications(feedTab === 'notifications' && canReadNetwork);

  return (
    // Two layout modes, switched at @5xl (the width where the map and the
    // activity card sit side by side):
    //   ≥@5xl — the card row takes the height left over after the headers and
    //           KPI row, floored at a usable minimum. On a tall viewport that
    //           fits exactly and nothing scrolls; on a short one the page
    //           scrolls only the small amount the floor actually needs —
    //           never a full extra screen.
    //   <@5xl — the cards stack, keep their own height floors, and the page
    //           scrolls as before.
    // `@container` only sizes descendants — an element cannot query its own
    // width — so the mode switch lives on the inner wrapper, not this root.
    <div className='@container flex flex-1 min-h-0 flex-col'>
      <div className='flex flex-1 min-h-0 flex-col gap-3 min-[1600px]:gap-4 overflow-y-auto p-4 min-[1600px]:p-6'>
        <div className='shrink-0 animate-in fade-in-0 slide-in-from-top-2 duration-400'>
          <h1 className='text-lg min-[1600px]:text-2xl font-bold tracking-tight text-foreground'>
            {t('welcome', { name: user?.firstName ?? '' })}{' '}
            <span role='img' aria-label='wave'>
              👋
            </span>
          </h1>
          <p className='text-xs min-[1600px]:text-sm text-muted-foreground mt-1'>
            {t('subtitle')}
          </p>
        </div>

        <div className='shrink-0 flex items-center justify-between'>
          <h2 className='text-base min-[1600px]:text-xl font-bold tracking-tight text-foreground'>
            {t('overview')}
          </h2>
          <button
            type='button'
            onClick={() => setHelpOpen(true)}
            className='flex items-center gap-1.5 text-xs min-[1600px]:text-sm text-primary hover:underline'
          >
            <FontAwesomeIcon icon={faCircleQuestion} className='text-base' />
            {t('help')}
          </button>
        </div>

        {canReadNetwork && (
          <KpiCards
            countryCount={countryCount}
            companyCount={companyCount}
            tasksData={tasksData}
            onOpenTasks={openActivityFeed}
          />
        )}

        <div className='grid grid-cols-1 gap-3 min-[1600px]:gap-4 @5xl:grid-cols-3 @5xl:flex-1 @5xl:min-h-[420px]'>
          {canReadNetwork ? (
            <>
              <MapCard
                companies={companies}
                loading={mapLoading}
                companyCount={companyCount}
              />
              <div
                className='flex flex-col h-full min-h-0 animate-in fade-in-0 duration-500'
                style={{ animationDelay: '160ms', animationFillMode: 'both' }}
              >
                <ActivityCard
                  tab={feedTab}
                  onTabChange={setFeedTab}
                  tasks={tasksData?.data ?? []}
                  notifications={notifications}
                  isNotificationsLoading={isNotificationsLoading}
                  onStatusChange={changeStatus}
                  onViewAll={openActivityFeed}
                />
              </div>
            </>
          ) : (
            // Every widget on this page (map, KPIs, tasks, notifications)
            // reads network-scoped endpoints, so a user without `network:read`
            // has nothing left to show here — an empty grid row would read as
            // broken rather than intentional.
            <div className='@5xl:col-span-3 flex items-center justify-center rounded-xl border border-border bg-card'>
              <EmptyState
                icon={<FontAwesomeIcon icon={faBuilding} className='size-5' />}
                title={t('noNetworkAccess.title')}
                description={t('noNetworkAccess.description')}
              />
            </div>
          )}
        </div>

        <HelpSheet
          articleId='8508473-3-important-icons-to-navigate-the-platform'
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
        />
      </div>
    </div>
  );
}
